export type Category =
  | 'frontend'
  | 'backend'
  | 'devops'
  | 'data'
  | 'mobile'
  | 'design'
  | 'business'
  | 'general'

export interface CategoryScore {
  word: string
  category: Category
  confidence: number
}

export interface CategoryStats {
  category: Category
  count: number
  totalOccurrences: number
  percentage: number
}

// Category taxonomy - words mapped to categories
const CATEGORY_KEYWORDS: Record<Category, string[]> = {
  frontend: [
    'react',
    'vue',
    'angular',
    'component',
    'jsx',
    'html',
    'css',
    'sass',
    'tailwind',
    'styled',
    'browser',
    'dom',
    'webpack',
    'vite',
    'nextjs',
    'gatsby',
    'svelte',
    'typescript',
    'javascript',
    'responsive',
    'layout',
    'animation',
    'interface',
    'button',
    'form',
    'modal',
    'navigation',
    'routing',
    'state',
    'hooks',
    'redux',
    'zustand',
    'context',
    'render',
    'virtual',
  ],
  backend: [
    'api',
    'server',
    'database',
    'sql',
    'nosql',
    'mongodb',
    'postgresql',
    'mysql',
    'redis',
    'graphql',
    'rest',
    'endpoint',
    'microservices',
    'service',
    'authentication',
    'authorization',
    'middleware',
    'node',
    'express',
    'fastapi',
    'django',
    'flask',
    'spring',
    'golang',
    'python',
    'java',
    'cache',
    'queue',
    'async',
    'sync',
    'orm',
    'migration',
    'schema',
    'query',
  ],
  devops: [
    'docker',
    'kubernetes',
    'deployment',
    'container',
    'orchestration',
    'cicd',
    'pipeline',
    'jenkins',
    'github',
    'gitlab',
    'actions',
    'terraform',
    'ansible',
    'infrastructure',
    'cloud',
    'aws',
    'azure',
    'gcp',
    'monitoring',
    'logging',
    'metrics',
    'observability',
    'prometheus',
    'grafana',
    'nginx',
    'load',
    'balancer',
    'scaling',
    'availability',
    'reliability',
    'security',
    'ssl',
    'https',
  ],
  data: [
    'data',
    'analytics',
    'machine',
    'learning',
    'model',
    'training',
    'dataset',
    'algorithm',
    'neural',
    'network',
    'tensorflow',
    'pytorch',
    'scikit',
    'pandas',
    'numpy',
    'visualization',
    'chart',
    'graph',
    'statistics',
    'analysis',
    'insight',
    'prediction',
    'classification',
    'regression',
    'clustering',
    'pipeline',
    'etl',
    'warehouse',
    'lake',
    'bigquery',
    'spark',
    'hadoop',
  ],
  mobile: [
    'mobile',
    'ios',
    'android',
    'swift',
    'kotlin',
    'flutter',
    'react-native',
    'native',
    'app',
    'application',
    'smartphone',
    'tablet',
    'touch',
    'gesture',
    'notification',
    'push',
    'offline',
    'cordova',
    'ionic',
    'xamarin',
  ],
  design: [
    'design',
    'user',
    'experience',
    'interface',
    'usability',
    'accessibility',
    'a11y',
    'figma',
    'sketch',
    'prototype',
    'wireframe',
    'mockup',
    'typography',
    'color',
    'theme',
    'brand',
    'visual',
    'aesthetic',
    'pattern',
    'consistency',
    'feedback',
    'interaction',
  ],
  business: [
    'business',
    'product',
    'strategy',
    'growth',
    'revenue',
    'customer',
    'user',
    'market',
    'startup',
    'enterprise',
    'team',
    'management',
    'leadership',
    'agile',
    'scrum',
    'sprint',
    'planning',
    'requirement',
    'stakeholder',
    'metric',
    'kpi',
    'roi',
    'conversion',
    'retention',
    'engagement',
  ],
  general: [
    'code',
    'programming',
    'development',
    'software',
    'engineering',
    'developer',
    'build',
    'create',
    'implement',
    'feature',
    'function',
    'method',
    'class',
    'object',
    'variable',
    'constant',
    'debug',
    'test',
    'testing',
    'unit',
    'integration',
    'refactor',
    'optimize',
    'performance',
    'quality',
    'documentation',
    'comment',
    'version',
    'git',
    'commit',
    'branch',
    'merge',
  ],
}

// Reverse mapping for faster lookup
const WORD_TO_CATEGORIES = new Map<string, Category[]>()
Object.entries(CATEGORY_KEYWORDS).forEach(([category, words]) => {
  words.forEach(word => {
    const existing = WORD_TO_CATEGORIES.get(word) || []
    existing.push(category as Category)
    WORD_TO_CATEGORIES.set(word, existing)
  })
})

export const CATEGORY_LABELS: Record<Category, string> = {
  frontend: 'Frontend',
  backend: 'Backend',
  devops: 'DevOps & Infrastructure',
  data: 'Data & ML',
  mobile: 'Mobile',
  design: 'Design & UX',
  business: 'Business & Product',
  general: 'General Development',
}

export const CATEGORY_COLORS: Record<Category, string> = {
  frontend: '#3b82f6', // blue
  backend: '#8b5cf6', // purple
  devops: '#f59e0b', // amber
  data: '#10b981', // green
  mobile: '#ec4899', // pink
  design: '#f97316', // orange
  business: '#06b6d4', // cyan
  general: '#6b7280', // gray
}

/**
 * Categorize a word based on keyword matching
 * @param word - The word to categorize
 * @returns Category classification with confidence
 */
export function categorizeWord(word: string): CategoryScore {
  const normalizedWord = word.toLowerCase().trim()

  // Direct match
  const categories = WORD_TO_CATEGORIES.get(normalizedWord)
  if (categories && categories.length > 0) {
    return {
      word,
      category: categories[0], // Take first category if multiple matches
      confidence: 1.0,
    }
  }

  // Partial match (word contains or is contained in keyword)
  let bestMatch: { category: Category; confidence: number } | null = null

  for (const [keyword, cats] of WORD_TO_CATEGORIES.entries()) {
    if (normalizedWord.includes(keyword) || keyword.includes(normalizedWord)) {
      const confidence =
        Math.min(normalizedWord.length, keyword.length) /
        Math.max(normalizedWord.length, keyword.length)

      if (!bestMatch || confidence > bestMatch.confidence) {
        bestMatch = { category: cats[0], confidence }
      }
    }
  }

  if (bestMatch) {
    return {
      word,
      category: bestMatch.category,
      confidence: bestMatch.confidence,
    }
  }

  // Default to general
  return {
    word,
    category: 'general',
    confidence: 0.1,
  }
}

/**
 * Categorize multiple words with their frequencies
 * @param words - Array of words with frequencies
 * @returns Array of words with category classifications
 */
export function categorizeWords(
  words: Array<{ text: string; value: number }>
): Array<{
  text: string
  value: number
  category: Category
  confidence: number
}> {
  return words.map(word => {
    const classification = categorizeWord(word.text)
    return {
      ...word,
      category: classification.category,
      confidence: classification.confidence,
    }
  })
}

/**
 * Calculate category statistics
 * @param words - Array of words with categories
 * @returns Category statistics
 */
export function calculateCategoryStats(
  words: Array<{ category: Category; value: number }>
): CategoryStats[] {
  const categoryMap = new Map<Category, { count: number; total: number }>()

  words.forEach(word => {
    const existing = categoryMap.get(word.category) || { count: 0, total: 0 }
    existing.count++
    existing.total += word.value
    categoryMap.set(word.category, existing)
  })

  const totalWords = words.length

  return Array.from(categoryMap.entries())
    .map(([category, stats]) => ({
      category,
      count: stats.count,
      totalOccurrences: stats.total,
      percentage: (stats.count / totalWords) * 100,
    }))
    .sort((a, b) => b.count - a.count)
}

/**
 * Get category color
 * @param category - The category
 * @returns CSS color string
 */
export function getCategoryColor(category: Category): string {
  return CATEGORY_COLORS[category]
}
